/**
 * Normalizes Quill / pasted HTML for public featured job display.
 * Converts emoji-"bullet" paragraphs into real lists, drops empty spacers,
 * and collapses duplicated company About Us blobs.
 */

const BULLET_PREFIX_PATTERN =
  /^(?:▪️|◾|◼|•|●|○|▪|▫|►|▸|–|—|-|📍|\u2022)\s*/u;

const SECTION_LABEL_PATTERN =
  /^(?:Position\s+)?(?:Responsibilities|Requirements|Qualifications|Preferred Qualifications|Basic Qualifications|Nice to [Hh]ave|Key (?:Job )?[Rr]esponsibilities|About(?:\s+Us)?|Description)\s*:?\s*$/u;

function normalizeText(value: string | null | undefined): string {
  return (value ?? '')
    .replace(/\u00a0/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function isEmptyBlock(element: Element): boolean {
  const text = normalizeText(element.textContent);
  if (text) {
    return false;
  }
  return !element.querySelector('img, video, iframe, table');
}

function stripBulletPrefix(text: string): string {
  return text.replace(BULLET_PREFIX_PATTERN, '').trim();
}

function isBulletParagraph(element: Element): boolean {
  if (element.tagName !== 'P') {
    return false;
  }
  return BULLET_PREFIX_PATTERN.test(normalizeText(element.textContent));
}

function isSectionLabel(element: Element): boolean {
  if (element.tagName !== 'P') {
    return false;
  }
  return SECTION_LABEL_PATTERN.test(normalizeText(element.textContent));
}

function createListFromParagraphs(
  doc: Document,
  paragraphs: Element[]
): HTMLUListElement {
  const list = doc.createElement('ul');
  for (const paragraph of paragraphs) {
    const item = doc.createElement('li');
    item.textContent = stripBulletPrefix(normalizeText(paragraph.textContent));
    list.appendChild(item);
  }
  return list;
}

function convertBulletParagraphs(container: HTMLElement, doc: Document): void {
  const children = Array.from(container.children);
  let index = 0;

  while (index < children.length) {
    const current = children[index];
    if (!isBulletParagraph(current)) {
      index += 1;
      continue;
    }

    const group: Element[] = [];
    let cursor = index;
    while (cursor < children.length && isBulletParagraph(children[cursor])) {
      group.push(children[cursor]);
      cursor += 1;
    }

    const list = createListFromParagraphs(doc, group);
    current.replaceWith(list);
    for (let offset = 1; offset < group.length; offset += 1) {
      group[offset].remove();
    }

    children.splice(index, group.length, list);
    index += 1;
  }
}

function promoteSectionLabels(container: HTMLElement, doc: Document): void {
  for (const child of Array.from(container.children)) {
    if (!isSectionLabel(child)) {
      continue;
    }
    const heading = doc.createElement('h3');
    heading.textContent = normalizeText(child.textContent).replace(/:$/, '');
    child.replaceWith(heading);
  }
}

function removeEmptyBlocks(container: HTMLElement): void {
  for (const child of Array.from(container.children)) {
    if (isEmptyBlock(child)) {
      child.remove();
    }
  }
}

/**
 * If the content repeats itself (common About Us paste bug), keep the first copy.
 */
function collapseDuplicatedContent(container: HTMLElement): void {
  const children = Array.from(container.children);
  if (children.length < 4) {
    return;
  }

  const texts = children.map((child) => normalizeText(child.textContent));
  const firstIdx = texts.findIndex((text) => text.length >= 24);
  if (firstIdx < 0) {
    return;
  }

  const needle = texts[firstIdx];
  let cutFrom = -1;

  for (let i = firstIdx + 1; i < texts.length; i += 1) {
    const text = texts[i];
    if (!text) {
      continue;
    }

    if (text === needle) {
      cutFrom = i;
      break;
    }

    const nestedAt = text.indexOf(needle);
    if (nestedAt > 0) {
      // "...recruitmentWe are a boutique..." — keep the prefix, drop the rest.
      children[i].textContent = text.slice(0, nestedAt).trim();
      cutFrom = i + 1;
      break;
    }
  }

  if (cutFrom < 0 || cutFrom >= children.length) {
    return;
  }

  for (let i = children.length - 1; i >= cutFrom; i -= 1) {
    children[i].remove();
  }
}

export function normalizeFeaturedRichHtml(
  html: string | undefined | null
): string {
  const source = html?.trim() ?? '';
  if (!source || typeof DOMParser === 'undefined') {
    return source;
  }

  const doc = new DOMParser().parseFromString(
    `<div id="root">${source}</div>`,
    'text/html'
  );
  const container = doc.getElementById('root');
  if (!container) {
    return source;
  }

  removeEmptyBlocks(container);
  convertBulletParagraphs(container, doc);
  promoteSectionLabels(container, doc);
  collapseDuplicatedContent(container);
  removeEmptyBlocks(container);

  return container.innerHTML.trim();
}
